import type {
  ColumnData,
  RoutineLabelPickerRequest,
  SlideLayoutLocationState,
} from '@gripp/shared-ui';
import {
  RoutineLabelPickerProvider,
  SlideAction,
  SlideLayout,
  useSlidePanel,
  ViewMode,
} from '@gripp/shared-ui';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { AssetRouteProvider } from '../assetRouteContext';
import { RoutineSubmitSource } from '../assetUtils';
import { AssetRoutineSubmitColumn } from './asset-routine-submit-column';
import { AssetRoutineSubmitHeader } from './asset-routine-submit-header';
import { RoutineAllColumn } from './asset-routines-all-column';
import { AssetRoutinesHeader } from './asset-routines-header';
import { AssetCard } from './assetCard';

type RoutineSubmitLocationState = SlideLayoutLocationState & {
  from?: string;
};

const toRoutineSubmitLocationState = (
  state: unknown
): RoutineSubmitLocationState | null =>
  state && typeof state === 'object' && !Array.isArray(state)
    ? (state as RoutineSubmitLocationState)
    : null;

const ASSET_ROUTINE_SUBMIT_SOURCE =
  'gripp-workspace/apps/gripp-web/src/assets/components/asset-routine-submit.tsx';
const ASSET_ROUTINE_SUBMIT_COLUMN_SOURCE =
  'gripp-workspace/apps/gripp-web/src/assets/components/asset-routine-submit-column.tsx';
const ASSET_ROUTINES_ALL_COLUMN_SOURCE =
  'gripp-workspace/apps/gripp-web/src/assets/components/asset-routines-all-column.tsx';
const ASSET_CARD_COMPONENT_SOURCE =
  'gripp-workspace/apps/gripp-web/src/assets/components/assetCard.tsx';
const ACTIVITY_ROUTES_SOURCE =
  'gripp-workspace/apps/gripp-web/src/activity/routes.tsx';
const ASSET_ROUTES_SOURCE =
  'gripp-workspace/apps/gripp-web/src/assets/routes.tsx';
const ACTIVITY_ASSET_ROUTINE_SUBMIT_ROUTE_PATTERN =
  '/activities/:issueId/asset/:assetId/routines/:routineId';
const ASSET_ROUTINE_SUBMIT_ROUTE_PATTERN =
  '/assets/:assetId/routines/:routineId';

const AssetRoutineSubmitRightPanel = ({
  column,
}: {
  column: ColumnData | null;
}) => {
  useSlidePanel('right', column, [column]);
  return null;
};

const AssetRoutineSubmitThirdPanel = ({
  column,
}: {
  column: ColumnData | null;
}) => {
  useSlidePanel('third', column, [column]);
  return null;
};

export const AssetRoutineSubmit = () => {
  const { assetId, routineId, issueId } = useParams();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const backGuardRef = useRef<
    ((onProceed: () => void, onCancel?: () => void) => void) | null
  >(null);
  const [, setLabelPickerRequest] = useState<RoutineLabelPickerRequest | null>(
    null
  );
  const locationState = toRoutineSubmitLocationState(location.state);
  const initialTransition =
    locationState?.slideLayoutInitialTransition === SlideAction.NEXT ||
    locationState?.slideLayoutInitialTransition === SlideAction.PREV
      ? locationState.slideLayoutInitialTransition
      : undefined;

  if (!assetId || !routineId) {
    return null;
  }

  const from = searchParams.get('from') ?? locationState?.from;
  const originalSource = from ?? RoutineSubmitSource.AssetCard;
  const cameFromRoutinesList =
    originalSource === RoutineSubmitSource.RoutinesList;
  const shouldUseActivityBackHandoff = !!issueId;

  const backButtonGuard = (): Promise<boolean> => {
    return new Promise<boolean>((resolve) => {
      if (!backGuardRef.current) {
        resolve(true);
        return;
      }
      backGuardRef.current(
        () => resolve(true),
        () => resolve(false)
      );
    });
  };

  const routePattern = issueId
    ? ACTIVITY_ASSET_ROUTINE_SUBMIT_ROUTE_PATTERN
    : ASSET_ROUTINE_SUBMIT_ROUTE_PATTERN;
  const routeSource = issueId ? ACTIVITY_ROUTES_SOURCE : ASSET_ROUTES_SOURCE;
  const routeSymbol = issueId ? 'activityRoutes' : 'assetRoutes';

  const leftColumn: ColumnData = {
    title: 'assets.routines',
    mainComponent: cameFromRoutinesList ? (
      <RoutineAllColumn assetId={assetId} />
    ) : (
      <AssetCard assetId={assetId} />
    ),
    headerComponent: <AssetRoutinesHeader assetId={assetId} />,
    showBackButton: true,
    backButtonTransition: SlideAction.PREV,
    backButtonTransitionBehavior: shouldUseActivityBackHandoff
      ? 'concurrent'
      : undefined,
    backButtonTransitionExitColumn: shouldUseActivityBackHandoff
      ? 'both'
      : undefined,
    backButtonGuard,
    onBackButtonPress: () =>
      navigate(issueId ? '../../../..' : '../..', {
        relative: 'path',
        ...(shouldUseActivityBackHandoff
          ? { state: { slideLayoutInitialTransition: SlideAction.PREV } }
          : {}),
      }),
    debug: cameFromRoutinesList
      ? {
          id: 'asset-routine-submit:left-routines-list',
          titleKey: 'assets.routines',
          component: 'RoutineAllColumn',
          componentSource: ASSET_ROUTINES_ALL_COLUMN_SOURCE,
          columnSource: ASSET_ROUTINE_SUBMIT_SOURCE,
          ownerComponent: 'AssetRoutineSubmit',
          ownerSource: ASSET_ROUTINE_SUBMIT_SOURCE,
          routePanelKey: issueId
            ? 'activity-asset-routines-list'
            : 'asset-routine-submit',
          routePattern,
          routeSource,
          source: {
            owner: {
              file: ASSET_ROUTINE_SUBMIT_SOURCE,
              symbol: 'AssetRoutineSubmit',
            },
            column: {
              file: ASSET_ROUTINE_SUBMIT_SOURCE,
              symbol: 'leftColumn',
            },
            component: {
              file: ASSET_ROUTINES_ALL_COLUMN_SOURCE,
              symbol: 'RoutineAllColumn',
            },
            route: {
              file: routeSource,
              symbol: routeSymbol,
            },
          },
          entity: { type: 'asset', id: assetId },
        }
      : {
          id: issueId
            ? 'activity-conversation:third-asset-card'
            : 'asset-routine-submit:left-asset-card',
          titleKey: issueId ? 'assets.menu.titleSingular' : 'assets.menu.title',
          component: 'AssetCard',
          componentSource: ASSET_CARD_COMPONENT_SOURCE,
          columnSource: ASSET_ROUTINE_SUBMIT_SOURCE,
          ownerComponent: 'AssetRoutineSubmit',
          ownerSource: ASSET_ROUTINE_SUBMIT_SOURCE,
          routePanelKey: issueId
            ? 'activity-asset-card'
            : 'asset-routine-submit',
          routePattern,
          routeSource,
          source: {
            owner: {
              file: ASSET_ROUTINE_SUBMIT_SOURCE,
              symbol: 'AssetRoutineSubmit',
            },
            column: {
              file: ASSET_ROUTINE_SUBMIT_SOURCE,
              symbol: 'leftColumn',
            },
            component: {
              file: ASSET_CARD_COMPONENT_SOURCE,
              symbol: 'AssetCard',
            },
            route: {
              file: routeSource,
              symbol: routeSymbol,
            },
          },
          entity: { type: 'asset', id: assetId },
        },
  };

  const rightColumn: ColumnData = {
    title: 'routines.editRoutines.editRoutinesButton',
    mainComponent: (
      <RoutineLabelPickerProvider onRequestChange={setLabelPickerRequest}>
        <AssetRoutineSubmitColumn
          assetId={assetId}
          routineId={routineId}
          issueId={issueId}
          source={originalSource}
          backGuardRef={backGuardRef}
        />
      </RoutineLabelPickerProvider>
    ),
    showBackButton: true,
    disableScroll: true,
    headerComponent: (
      <AssetRoutineSubmitHeader assetId={assetId} routineId={routineId} />
    ),
    backButtonTransition: SlideAction.PREV,
    backButtonGuard,
    backButtonTransitionBehavior: 'concurrent',
    debug: {
      id: issueId ? 'activity-asset-routine-submit' : 'asset-routine-submit',
      titleKey: 'routines.editRoutines.editRoutinesButton',
      component: 'AssetRoutineSubmitColumn',
      componentSource: ASSET_ROUTINE_SUBMIT_COLUMN_SOURCE,
      columnSource: ASSET_ROUTINE_SUBMIT_SOURCE,
      ownerComponent: 'AssetRoutineSubmit',
      ownerSource: ASSET_ROUTINE_SUBMIT_SOURCE,
      routePanelKey: issueId
        ? 'activity-asset-routine-submit'
        : 'asset-routine-submit',
      routePattern,
      routeSource,
      source: {
        owner: {
          file: ASSET_ROUTINE_SUBMIT_SOURCE,
          symbol: 'AssetRoutineSubmit',
        },
        column: {
          file: ASSET_ROUTINE_SUBMIT_SOURCE,
          symbol: 'rightColumn',
        },
        component: {
          file: ASSET_ROUTINE_SUBMIT_COLUMN_SOURCE,
          symbol: 'AssetRoutineSubmitColumn',
        },
        route: {
          file: routeSource,
          symbol: routeSymbol,
        },
      },
      entity: { type: 'routine', id: routineId },
    },
    onBackButtonPress: () => {
      const url = cameFromRoutinesList ? '../all' : '../..';
      navigate(url, {
        relative: 'path',
        state: { slideLayoutInitialTransition: SlideAction.PREV },
      });
    },
  };

  return (
    <AssetRouteProvider
      value={{
        basePath: issueId
          ? `/activities/${issueId}/asset/${assetId}`
          : `/assets/${assetId}`,
        listPath: issueId ? '/activities' : '/assets',
        isWorkspaceMode: false,
        issueId,
      }}
    >
      <SlideLayout
        leftColumn={leftColumn}
        rightColumn={rightColumn}
        viewMode={ViewMode.DUAL}
        initialTransition={initialTransition}
      />
    </AssetRouteProvider>
  );
};

export const AssetRoutineSubmitPanel = () => {
  const { assetId, routineId } = useParams();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const backGuardRef = useRef<
    ((onProceed: () => void, onCancel?: () => void) => void) | null
  >(null);
  const [, setLabelPickerRequest] = useState<RoutineLabelPickerRequest | null>(
    null
  );
  const locationState = toRoutineSubmitLocationState(location.state);

  const from = searchParams.get('from') ?? locationState?.from;
  const originalSource = from ?? RoutineSubmitSource.AssetCard;
  const cameFromRoutinesList =
    originalSource === RoutineSubmitSource.RoutinesList;

  const backButtonGuard = useCallback((): Promise<boolean> => {
    return new Promise<boolean>((resolve) => {
      if (!backGuardRef.current) {
        resolve(true);
        return;
      }
      backGuardRef.current(
        () => resolve(true),
        () => resolve(false)
      );
    });
  }, []);

  const rightColumn = useMemo<ColumnData | null>(() => {
    if (!assetId || !cameFromRoutinesList) return null;
    return {
      title: 'asset.header.buttons.routines',
      mainComponent: <RoutineAllColumn assetId={assetId} />,
      headerComponent: <AssetRoutinesHeader assetId={assetId} />,
      showBackButton: true,
      backButtonTransition: SlideAction.PREV,
      backButtonTransitionBehavior: 'concurrent',
      backButtonTransitionExitColumn: 'both',
      onBackButtonPress: () =>
        navigate(`/assets/${assetId}`, {
          state: { slideLayoutInitialTransition: SlideAction.PREV },
        }),
      disableScroll: true,
      debug: {
        id: 'asset-routines-list',
        titleKey: 'asset.header.buttons.routines',
        component: 'RoutineAllColumn',
        componentSource:
          'gripp-workspace/apps/gripp-web/src/assets/components/asset-routines-all-column.tsx',
        columnSource: ASSET_ROUTINE_SUBMIT_SOURCE,
        ownerComponent: 'AssetRoutineSubmitPanel',
        ownerSource: ASSET_ROUTINE_SUBMIT_SOURCE,
        routePanelKey: 'asset-routine-submit',
        routePattern: '/assets/:assetId/routines/:routineId',
        routeSource: ASSET_ROUTES_SOURCE,
        entity: { type: 'asset', id: assetId },
      },
    };
  }, [assetId, cameFromRoutinesList, navigate]);

  const thirdColumn = useMemo<ColumnData | null>(() => {
    if (!assetId || !routineId) return null;

    return {
      title: 'routines.editRoutines.editRoutinesButton',
      mainComponent: (
        <RoutineLabelPickerProvider onRequestChange={setLabelPickerRequest}>
          <AssetRoutineSubmitColumn
            assetId={assetId}
            routineId={routineId}
            source={originalSource}
            backGuardRef={backGuardRef}
            animateOnReturn={false}
          />
        </RoutineLabelPickerProvider>
      ),
      showBackButton: true,
      disableScroll: true,
      backButtonTransition: SlideAction.PREV,
      backButtonTransitionBehavior: 'concurrent',
      headerComponent: (
        <AssetRoutineSubmitHeader assetId={assetId} routineId={routineId} />
      ),
      backButtonGuard,
      onBackButtonPress: () => {
        navigate(cameFromRoutinesList ? '../all' : '../..', {
          relative: 'path',
          state: { slideLayoutInitialTransition: SlideAction.PREV },
        });
      },
      debug: {
        id: 'asset-routine-submit',
        titleKey: 'routines.editRoutines.editRoutinesButton',
        component: 'AssetRoutineSubmitColumn',
        componentSource: ASSET_ROUTINE_SUBMIT_COLUMN_SOURCE,
        columnSource: ASSET_ROUTINE_SUBMIT_SOURCE,
        ownerComponent: 'AssetRoutineSubmitPanel',
        ownerSource: ASSET_ROUTINE_SUBMIT_SOURCE,
        routePanelKey: 'asset-routine-submit',
        routePattern: '/assets/:assetId/routines/:routineId',
        routeSource: ASSET_ROUTES_SOURCE,
        entity: { type: 'routine', id: routineId },
      },
    };
  }, [
    assetId,
    routineId,
    originalSource,
    cameFromRoutinesList,
    navigate,
    backButtonGuard,
  ]);

  return (
    <>
      {cameFromRoutinesList && (
        <AssetRoutineSubmitRightPanel column={rightColumn} />
      )}
      <AssetRoutineSubmitThirdPanel column={thirdColumn} />
    </>
  );
};
